import { Button, Popover, Spinner } from '@heroui/react'
import { Bell, CheckCheck } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../../context/AuthContext'
import { useActiveScope } from '../../context/ScopeContext'
import { NOTIFICATION_DELIVERY_PERMISSIONS } from '../../services/notification.api'
import {
  getUserNotifications,
  markAllUserNotificationsAsRead,
  markUserNotificationAsRead,
  type UserNotificationItem,
} from '../../services/userNotifications.api'

function formatTime(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
  }).format(parsed)
}

export function NotificationBell() {
  const { user, hasAnyPermission } = useAuth()
  const { scopeKey } = useActiveScope()
  const navigate = useNavigate()

  // Hanya tampilkan tautan ke log pengiriman global jika memiliki permission terkait
  const canReadDeliveries = hasAnyPermission(...NOTIFICATION_DELIVERY_PERMISSIONS)

  const [notifications, setNotifications] = useState<UserNotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')

  const userId = user?.id

  const load = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setFailed(false)
    try {
      const res = await getUserNotifications(30, 0)
      const items = res?.items ?? []
      setNotifications(items)
      setUnreadCount(
        typeof res?.unreadCount === 'number'
          ? res.unreadCount
          : items.filter((n) => !n.isRead).length,
      )
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void load()
  }, [load, scopeKey])

  const handleMarkAsRead = async (item: UserNotificationItem) => {
    if (item.isRead) return
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === item.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)),
    )
    setUnreadCount((prev) => Math.max(0, prev - 1))
    try {
      await markUserNotificationAsRead(item.id)
    } catch {
      // ignore
    }
  }

  const handleItemClick = async (item: UserNotificationItem) => {
    await handleMarkAsRead(item)
    if (item.actionUrl) {
      let targetUrl = item.actionUrl
      if (targetUrl === '/app/spmb/applicants' || targetUrl === '/app/spmb/registrations') {
        const regNo =
          (item.metadata && typeof item.metadata === 'object' && 'registrationNumber' in item.metadata
            ? String((item.metadata as { registrationNumber?: unknown }).registrationNumber)
            : null) || item.message?.match(/\((REG-[^)]+)\)/)?.[1]

        if (regNo) {
          targetUrl = `/app/spmb/registrations?search=${encodeURIComponent(regNo)}`
        } else {
          targetUrl = '/app/spmb/registrations'
        }
      } else if (targetUrl.startsWith('/app/spmb/applicants')) {
        targetUrl = targetUrl.replace('/app/spmb/applicants', '/app/spmb/registrations')
      }
      navigate(targetUrl)
    }
  }

  const handleMarkAllAsRead = async () => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() })),
    )
    setUnreadCount(0)
    try {
      await markAllUserNotificationsAsRead()
    } catch {
      // ignore
    }
  }

  const displayedNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter((item) => !item.isRead)
    }
    return notifications
  }, [activeTab, notifications])

  if (!user) return null

  return (
    <Popover>
      <Popover.Trigger
        tabIndex={0}
        data-testid="shell-notification-bell-btn"
        aria-label={
          unreadCount > 0
            ? `Notifikasi, ${unreadCount} belum dibaca`
            : 'Notifikasi'
        }
        className="relative inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-wicend-500 focus-visible:outline-offset-2 dark:text-slate-300 dark:hover:bg-slate-900"
      >
        <Bell size={18} aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-wicend-600 px-1 text-[10px] font-bold text-white shadow-sm">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Popover.Trigger>

      <Popover.Content className="w-88 max-w-[calc(100vw-2rem)]">
        <Popover.Dialog className="p-0" data-testid="shell-notification-panel">
          {/* Header */}
          <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-slate-800 dark:text-white">Notifikasi</p>
                {unreadCount > 0 && (
                  <span className="rounded-full bg-wicend-50 px-2 py-0.5 text-[10px] font-bold text-wicend-700 dark:bg-wicend-950/60 dark:text-wicend-300">
                    {unreadCount} baru
                  </span>
                )}
              </div>

              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  data-testid="shell-notification-mark-all-btn"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-wicend-600 hover:text-wicend-700 dark:text-wicend-400 dark:hover:text-wicend-300"
                  title="Tandai semua telah dibaca"
                >
                  <CheckCheck size={13} />
                  <span>Tandai dibaca</span>
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="mt-2.5 flex items-center gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800/80">
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                data-testid="shell-notification-all-tab"
                className={`flex-1 rounded-md py-1 text-center text-xs font-semibold transition-colors ${
                  activeTab === 'all'
                    ? 'bg-white text-slate-800 shadow-xs dark:bg-slate-700 dark:text-white'
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                Semua ({notifications.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('unread')}
                data-testid="shell-notification-unread-tab"
                className={`flex-1 rounded-md py-1 text-center text-xs font-semibold transition-colors ${
                  activeTab === 'unread'
                    ? 'bg-white text-slate-800 shadow-xs dark:bg-slate-700 dark:text-white'
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                Belum Dibaca ({unreadCount})
              </button>
            </div>
          </div>

          {/* List Area */}
          <div className="max-h-84 overflow-y-auto">
            {loading && (
              <div className="flex justify-center py-8">
                <Spinner size="sm" />
              </div>
            )}

            {!loading && failed && (
              <p className="px-4 py-6 text-center text-xs text-slate-500">
                Data notifikasi belum dapat dimuat.
              </p>
            )}

            {!loading && !failed && displayedNotifications.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  {activeTab === 'unread'
                    ? 'Semua notifikasi telah dibaca.'
                    : 'Belum ada notifikasi.'}
                </p>
              </div>
            )}

            {!loading &&
              !failed &&
              displayedNotifications.map((item) => {
                const isRead = item.isRead

                return (
                  <div
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleItemClick(item)
                      }
                    }}
                    className={`group relative flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-b-0 dark:border-slate-800/60 ${
                      !isRead
                        ? 'bg-sky-50/40 hover:bg-sky-50/70 dark:bg-sky-950/20 dark:hover:bg-sky-950/30'
                        : 'bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/40'
                    }`}
                  >
                    {/* Unread indicator dot */}
                    <div className="mt-1 flex shrink-0 items-center justify-center">
                      {!isRead ? (
                        <span
                          className="h-2 w-2 rounded-full bg-wicend-500"
                          title="Belum dibaca"
                        />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-transparent" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-1.5">
                        <p
                          className={`truncate text-xs ${
                            !isRead
                              ? 'font-bold text-slate-900 dark:text-white'
                              : 'font-medium text-slate-700 dark:text-slate-300'
                          }`}
                          title={item.title}
                        >
                          {item.title}
                        </p>
                        <span className="shrink-0 text-[10px] font-semibold text-slate-400 dark:text-slate-500">
                          {item.category}
                        </span>
                      </div>

                      <p className="mt-0.5 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">
                        {item.message}
                      </p>

                      <div className="mt-1 flex items-center justify-between text-[10px] text-slate-400 dark:text-slate-500">
                        <span>{formatTime(item.createdAt)}</span>

                        {!isRead && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              void handleMarkAsRead(item)
                            }}
                            data-testid="shell-notification-item-mark-read-btn"
                            className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-wicend-600 dark:hover:text-wicend-400 font-medium"
                            title="Tandai dibaca"
                          >
                            Tandai dibaca
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>

          {/* Footer for Delivery Logs */}
          {canReadDeliveries && (
            <div className="border-t border-slate-200 p-2 dark:border-slate-800">
              <Button
                size="sm"
                variant="ghost"
                className="w-full text-xs font-semibold"
                onPress={() => navigate('/app/spmb/notification-deliveries')}
                data-testid="shell-notification-deliveries-link-btn"
              >
                Lihat semua riwayat pengiriman
              </Button>
            </div>
          )}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  )
}
